<template>
  <SNavHeader :links="menu.tabs" :size="navSize" :overline="menu.overline">
    <template #heading>
      <s-link v-if="menu.headingHref" :href="menu.headingHref" class="link-ghost">
        {{ menu.heading }}
      </s-link>
      <template v-else>{{ menu.heading }}</template>
    </template>
    <template #actions>
      <template v-for="(action, index) in actions" :key="index">
        <component
          v-if="isComponentAction(action)"
          :is="action.component"
          v-bind="action.props"
          :size="actionSize"
        />
        <s-dropdown-menu v-else-if="isActionGroup(action)" :items="action.actions">
          <template #trigger>
            <s-btn :icon="action.icon" :size="actionSize" color="white">
              {{ action.label }}
            </s-btn>
          </template>
        </s-dropdown-menu>
        <s-btn
          v-else
          :icon="action.icon"
          :href="action.href"
          :external="action.external"
          :size="actionSize"
          :color="action.color as ButtonColor"
        >
          {{ action.label }}
        </s-btn>
      </template>
      <admin-button v-if="menu.adminRoute" :href="menu.adminRoute" :size="actionSize" />
    </template>
  </SNavHeader>
</template>
<script setup lang="ts">
import {computed} from 'vue';
import SNavHeader from './SNavHeader.vue';
import SBtn from './SBtn.vue';
import SDropdownMenu from './SDropdownMenu.vue';
import AdminButton from '../buttons/AdminButton.vue';
import SLink from '../components/SLink.vue';
import {useWindowSize} from '@vueuse/core';
import {ButtonColor} from './SBtn.d';
import {isActionGroup, isComponentAction, type PageHeaderAction} from './action';
import {isActionComponent, resolveActionComponent} from '../menus/action-components';
import TabMenu = App.DTOs.Menus.TabMenu;
import ActionComponent = App.DTOs.Menus.ActionComponent;

const props = defineProps<{
  menu: TabMenu;
}>();

type HeaderAction = PageHeaderAction | ActionComponent;

// `menu.actions` is typed as links and components, but consumers may also pass
// action groups (rendered as dropdowns), mirroring SPageHeader. Component
// actions are resolved to the component they name; an unregistered name is
// dropped (and reported to Sentry) instead of rendering an empty button. They
// render alongside links, so they're bound `actionSize` too — a registered
// component declares a `size` prop to stay the same height as its neighbours.
const actions = computed((): PageHeaderAction[] =>
  ((props.menu.actions ?? []) as unknown as HeaderAction[])
    .map((action): PageHeaderAction | null => {
      if (!isActionComponent(action)) return action;

      const component = resolveActionComponent(action.name);

      return component ? {component, props: action.props} : null;
    })
    .filter((action): action is PageHeaderAction => action !== null)
);

const {width: windowWidth} = useWindowSize();

const actionSize = computed(() => (windowWidth.value >= 768 ? 'md' : 'sm'));

const navSize = computed((): 'full' | 'lg' | 'md' | 'sm' | 'xs' => {
  return (props.menu.size || undefined) as 'full' | 'lg' | 'md' | 'sm' | 'xs';
});
</script>
